/**
 * Prayer Requests Admin Page
 *
 * Manage prayer requests from all customer portals.
 * View, pray over, and follow up on requests.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { Heart, Send, User, Calendar, Filter, Search, Mail, Check, RefreshCw, X } from 'lucide-react';
import api from '../../services/api';
import './PrayerRequests.css';

interface PrayerRequest {
  id: number;
  created_at: string;
  portal_source: string;
  portal_source_display: string;
  customer_name: string;
  anonymous: boolean;
  request_preview: string;
  status: string;
  status_display: string;
  allow_follow_up: boolean;
  has_email: boolean;
}

interface PrayerRequestDetail extends PrayerRequest {
  request_text: string;
  customer_email: string | null;
  admin_notes: string | null;
  prayed_at: string | null;
  prayed_by: string | null;
  updated_at: string;
  linked_customer?: { id: number; name: string; type: string };
  linked_bridge_club_member?: { id: number; name: string };
  linked_store_club_member?: { id: number; name: string };
}

interface Stats {
  total: number;
  new: number;
  prayed: number;
  followed_up: number;
  this_week: number;
  this_month: number;
  awaiting_follow_up: number;
  by_portal: Array<{ portal_source: string; count: number }>;
}

interface Counts {
  total: number;
  new: number;
  prayed: number;
  followed_up: number;
}

const PORTAL_COLORS: Record<string, string> = {
  bridge_club: '#7c3aed',
  store_club: '#059669',
  wholesale: '#2563eb',
  retail_partner: '#dc2626',
};

const STATUS_COLORS: Record<string, string> = {
  new: '#f59e0b',
  prayed: '#10b981',
  followed_up: '#6366f1',
};

const PrayerRequests: React.FC = () => {
  const [requests, setRequests] = useState<PrayerRequest[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<PrayerRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [portalFilter, setPortalFilter] = useState('');
  // Server search: input binds to `searchInput` (instant); debounced `searchQuery`
  // (400ms) drives the requests fetch so typing fires one request after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: searchQuery } = useDebouncedSearch('', 400);

  // Detail panel state
  const [adminNotes, setAdminNotes] = useState('');
  const [prayedBy, setPrayedBy] = useState('');
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingFollowUp, setIsSendingFollowUp] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (portalFilter) params.append('portal_source', portalFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(`/admin/prayer-requests/?${params.toString()}`);
      setRequests(response.data.results);
      setCounts(response.data.counts);
    } catch (err) {
      console.error('Failed to load prayer requests:', err);
    }
  }, [statusFilter, portalFilter, searchQuery]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/prayer-requests/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadRequestDetail = async (id: number) => {
    try {
      const response = await api.get(`/admin/prayer-requests/${id}/`);
      setSelectedRequest(response.data);
      setAdminNotes(response.data.admin_notes || '');
      setPrayedBy(response.data.prayed_by || '');
      setShowDetail(true);
      setShowFollowUp(false);
    } catch (err) {
      console.error('Failed to load request detail:', err);
    }
  };

  const updateRequest = async (status?: string) => {
    if (!selectedRequest) return;
    setIsUpdating(true);

    try {
      const data: any = { admin_notes: adminNotes };
      if (status) data.status = status;
      if (prayedBy) data.prayed_by = prayedBy;

      await api.patch(`/admin/prayer-requests/${selectedRequest.id}/update/`, data);

      // Reload
      await loadRequestDetail(selectedRequest.id);
      await loadRequests();
      await loadStats();
    } catch (err) {
      console.error('Failed to update request:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const sendFollowUp = async () => {
    if (!selectedRequest || !followUpMessage.trim()) return;
    setIsSendingFollowUp(true);

    try {
      await api.post(`/admin/prayer-requests/${selectedRequest.id}/follow-up/`, {
        message: followUpMessage,
      });

      // Reload
      await loadRequestDetail(selectedRequest.id);
      await loadRequests();
      await loadStats();
      setShowFollowUp(false);
      setFollowUpMessage('');
    } catch (err) {
      console.error('Failed to send follow-up:', err);
    } finally {
      setIsSendingFollowUp(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadRequests(), loadStats()]);
      setLoading(false);
    };
    init();
  }, [loadRequests, loadStats]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="prayer-admin">
        <div className="prayer-admin-loading">
          <Heart className="prayer-admin-loading-icon" />
          <p>Loading prayer requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prayer-admin">
      {/* Header */}
      <div className="prayer-admin-header">
        <div className="prayer-admin-title">
          <Heart className="prayer-admin-title-icon" />
          <h1>Prayer Requests</h1>
        </div>
        <button className="prayer-admin-refresh" onClick={() => { loadRequests(); loadStats(); }}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="prayer-admin-stats">
          <div className="prayer-stat-card prayer-stat-new">
            <div className="prayer-stat-value">{stats.new}</div>
            <div className="prayer-stat-label">New Requests</div>
          </div>
          <div className="prayer-stat-card prayer-stat-prayed">
            <div className="prayer-stat-value">{stats.prayed}</div>
            <div className="prayer-stat-label">Prayed</div>
          </div>
          <div className="prayer-stat-card prayer-stat-followed">
            <div className="prayer-stat-value">{stats.followed_up}</div>
            <div className="prayer-stat-label">Followed Up</div>
          </div>
          <div className="prayer-stat-card prayer-stat-week">
            <div className="prayer-stat-value">{stats.this_week}</div>
            <div className="prayer-stat-label">This Week</div>
          </div>
          {stats.awaiting_follow_up > 0 && (
            <div className="prayer-stat-card prayer-stat-awaiting">
              <div className="prayer-stat-value">{stats.awaiting_follow_up}</div>
              <div className="prayer-stat-label">Awaiting Follow-up</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="prayer-admin-filters">
        <div className="prayer-filter-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search requests..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="prayer-filter-select"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="prayed">Prayed</option>
          <option value="followed_up">Followed Up</option>
        </select>

        <select
          value={portalFilter}
          onChange={(e) => setPortalFilter(e.target.value)}
          className="prayer-filter-select"
        >
          <option value="">All Portals</option>
          <option value="bridge_club">Bridge Club</option>
          <option value="store_club">Store Club</option>
          <option value="wholesale">Wholesale</option>
          <option value="retail_partner">Retail Partner</option>
        </select>
      </div>

      {/* Content Grid */}
      <div className="prayer-admin-content">
        {/* Request List */}
        <div className="prayer-admin-list">
          {requests.length === 0 ? (
            <div className="prayer-admin-empty">
              <Heart size={48} />
              <p>No prayer requests found</p>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className={`prayer-request-card ${selectedRequest?.id === request.id ? 'selected' : ''}`}
                onClick={() => loadRequestDetail(request.id)}
              >
                <div className="prayer-request-header">
                  <span
                    className="prayer-request-portal"
                    style={{ background: PORTAL_COLORS[request.portal_source] || '#666' }}
                  >
                    {request.portal_source_display}
                  </span>
                  <span
                    className="prayer-request-status"
                    style={{ background: STATUS_COLORS[request.status] || '#666' }}
                  >
                    {request.status_display}
                  </span>
                </div>

                <div className="prayer-request-from">
                  <User size={14} />
                  <span>{request.customer_name}</span>
                </div>

                <p className="prayer-request-preview">{request.request_preview}</p>

                <div className="prayer-request-meta">
                  <Calendar size={14} />
                  <span>{formatDate(request.created_at)}</span>
                  {request.allow_follow_up && request.has_email && (
                    <Mail size={14} className="prayer-request-followup-icon" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {showDetail && selectedRequest && (
          <div className="prayer-admin-detail">
            <button className="prayer-detail-close" onClick={() => setShowDetail(false)}>
              <X size={20} />
            </button>

            <div className="prayer-detail-header">
              <span
                className="prayer-request-portal"
                style={{ background: PORTAL_COLORS[selectedRequest.portal_source] || '#666' }}
              >
                {selectedRequest.portal_source_display}
              </span>
              <span
                className="prayer-request-status"
                style={{ background: STATUS_COLORS[selectedRequest.status] || '#666' }}
              >
                {selectedRequest.status_display}
              </span>
            </div>

            <div className="prayer-detail-from">
              <User size={16} />
              <span>{selectedRequest.customer_name}</span>
              {selectedRequest.customer_email && (
                <span className="prayer-detail-email">{selectedRequest.customer_email}</span>
              )}
            </div>

            <div className="prayer-detail-date">
              <Calendar size={16} />
              <span>{formatDate(selectedRequest.created_at)}</span>
            </div>

            <div className="prayer-detail-text">
              <h3>Prayer Request</h3>
              <p>{selectedRequest.request_text}</p>
            </div>

            {selectedRequest.prayed_at && (
              <div className="prayer-detail-prayed">
                <Check size={16} />
                <span>
                  Prayed on {formatDate(selectedRequest.prayed_at)}
                  {selectedRequest.prayed_by && ` by ${selectedRequest.prayed_by}`}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="prayer-detail-actions">
              {selectedRequest.status === 'new' && (
                <div className="prayer-action-group">
                  <label>Prayed by:</label>
                  <select
                    value={prayedBy}
                    onChange={(e) => setPrayedBy(e.target.value)}
                    className="prayer-select"
                  >
                    <option value="">Select...</option>
                    <option value="Brad">Brad</option>
                    <option value="Bekah">Bekah</option>
                    <option value="Brad & Bekah">Brad & Bekah</option>
                    <option value="Team">Team</option>
                  </select>
                  <button
                    className="prayer-btn-mark-prayed"
                    onClick={() => updateRequest('prayed')}
                    disabled={isUpdating || !prayedBy}
                  >
                    <Check size={16} />
                    Mark as Prayed
                  </button>
                </div>
              )}

              {selectedRequest.allow_follow_up &&
                selectedRequest.customer_email &&
                selectedRequest.status !== 'followed_up' && (
                  <div className="prayer-action-group">
                    {!showFollowUp ? (
                      <button
                        className="prayer-btn-follow-up"
                        onClick={() => setShowFollowUp(true)}
                      >
                        <Send size={16} />
                        Send Follow-up Email
                      </button>
                    ) : (
                      <div className="prayer-follow-up-form">
                        <textarea
                          value={followUpMessage}
                          onChange={(e) => setFollowUpMessage(e.target.value)}
                          placeholder="Write your follow-up message..."
                          rows={5}
                        />
                        <div className="prayer-follow-up-actions">
                          <button
                            className="prayer-btn-cancel"
                            onClick={() => {
                              setShowFollowUp(false);
                              setFollowUpMessage('');
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="prayer-btn-send"
                            onClick={sendFollowUp}
                            disabled={isSendingFollowUp || !followUpMessage.trim()}
                          >
                            {isSendingFollowUp ? 'Sending...' : 'Send Email'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            {/* Admin Notes */}
            <div className="prayer-detail-notes">
              <h4>Admin Notes</h4>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this request..."
                rows={3}
              />
              <button
                className="prayer-btn-save-notes"
                onClick={() => updateRequest()}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save Notes'}
              </button>
            </div>

            {/* Linked Records */}
            {(selectedRequest.linked_customer ||
              selectedRequest.linked_bridge_club_member ||
              selectedRequest.linked_store_club_member) && (
              <div className="prayer-detail-linked">
                <h4>Linked Account</h4>
                {selectedRequest.linked_customer && (
                  <p>
                    Customer: {selectedRequest.linked_customer.name} (
                    {selectedRequest.linked_customer.type})
                  </p>
                )}
                {selectedRequest.linked_bridge_club_member && (
                  <p>Bridge Club: {selectedRequest.linked_bridge_club_member.name}</p>
                )}
                {selectedRequest.linked_store_club_member && (
                  <p>Store Club: {selectedRequest.linked_store_club_member.name}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrayerRequests;
